import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import "../Accounts/ledger-page.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import useApi from "../../hook/useApi";

const SortHint = () => (
  <Icon
    icon="tabler:chevron-down"
    className="icon ms-1 text-secondary align-middle"
    style={{ fontSize: "0.65rem" }}
  />
);

const CashEditDeleteLogLayer = () => {
  const { AsyncGetApiCall } = useApi();

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });
  const [filteredRows, setFilteredRows] = useState([]);

  const uniqueClientOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    filteredRows.forEach((entry) => {
      const label = entry?.name || "Unknown";
      const value = entry?.userId || entry?._id;
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label });
    });
    return options;
  }, [filteredRows]);

  const [searchTerm, setSearchTerm] = useState("");
  const [limitMode, setLimitMode] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);

  const [pageMeta, setPageMeta] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const limitVal = limitMode === "all" ? 1000 : Number(limitMode);

  const fetchCashLogs = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        pageNo,
        limitVal,
      };

      if (searchTerm.trim()) {
        payload.search = searchTerm.trim();
      }

      if (filters.fromDate) {
        payload.fromDate = filters.fromDate;
      }

      if (filters.toDate) {
        payload.toDate = filters.toDate;
      }

      const result = await AsyncGetApiCall(
        "/accounts/get-cash-logs",
        payload
      );

      if (result?.success) {
        setRows(Array.isArray(result.data) ? result.data : []);

        setPageMeta({
          total: Number(result.total) || 0,
          totalPages: Number(result.totalPages) || 0,
          currentPage: Number(result.currentPage) || pageNo,
          hasNextPage: Boolean(result.hasNextPage),
          hasPrevPage: Boolean(result.hasPrevPage),
        });
      } else {
        setRows([]);

        setPageMeta({
          total: 0,
          totalPages: 0,
          currentPage: 1,
          hasNextPage: false,
          hasPrevPage: false,
        });
      }
    } catch (error) {
      console.error("Failed to fetch cash logs", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    AsyncGetApiCall,
    filters.fromDate,
    filters.toDate,
    limitVal,
    pageNo,
    searchTerm,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCashLogs();
    }, 350);

    return () => clearTimeout(timer);
  }, [fetchCashLogs]);

  const total = pageMeta.total || rows.length;
  const hasPrev = pageMeta.hasPrevPage;
  const hasNext = pageMeta.hasNextPage;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPageNo(1);
  };

  const handleApply = () => {
    setPageNo(1);
    fetchCashLogs();
  };

  const handleClear = () => {
    setFilters({
      fromDate: "",
      toDate: "",
    });

    setSearchTerm("");
    setPageNo(1);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";

    const dt = new Date(value);

    if (Number.isNaN(dt.getTime())) return "—";

    return dt.toLocaleString("en-IN");
  };

  const formatAmount = (value) => {
    if (value == null) return "—";

    const num = Number(value);

    if (Number.isNaN(num)) return "—";

    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const startEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + 1;

  const endEntry =
    total === 0
      ? 0
      : (pageMeta.currentPage - 1) * limitVal + rows.length;

  return (
    <div className="card border shadow-sm radius-16 ledger-table-card">
      <div className="card-body p-20 border-bottom position-relative" style={{ zIndex: 10 }}>
        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3">
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                Show
              </label>

              <div style={{ width: "78px" }}>
                <SearchableSelect
                  placeholder="All"
                  value={limitMode}
                  showSearch={false}
                  onChange={(e) => {
                    setLimitMode(e.target.value);
                    setPageNo(1);
                  }}
                  options={[
                    { label: "All", value: "all" },
                    ...PAGE_SIZE_OPTIONS.map((n) => ({
                      label: String(n),
                      value: String(n),
                    })),
                  ]}
                />
              </div>
            </div>

            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                Select client
              </label>
              <div style={{ minWidth: "180px" }}>
                <SearchableSelect
                  placeholder="All users"
                  value={filters.client}
                  showSearch={true}
                  onChange={handleFilterChange}
                  options={uniqueClientOptions.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                  }))}
                />
              </div>
            </div>

            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                From date
              </label>

              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="form-control radius-8 h-40-px"
                style={{ width: "140px" }}
              />
            </div>

            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                To date
              </label>

              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="form-control radius-8 h-40-px"
                style={{ width: "140px" }}
              />
            </div>

            <button
              type="button"
              onClick={handleApply}
              className="btn btn-sm radius-8 px-16 py-10 btn-primary-600"
              disabled={loading}
            >
              Filter Results
            </button>

            <button
              type="button"
              onClick={handleClear}
              className="btn btn-sm radius-8 px-16 py-10 btn-danger-600"
              disabled={loading}
            >
              Clear Filters
            </button>
          </div>

          <div
            className="position-relative flex-grow-1"
            style={{ maxWidth: "280px" }}
          >
            <Icon
              icon="ion:search-outline"
              className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
            />

            <input
              type="search"
              className="form-control ps-40 h-40-px radius-12"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="table-responsive p-24">
        <table className="table bordered-table sm-table mb-0 text-nowrap">
          <thead>
            <tr className="bg-neutral-100">
              <th className="fw-bold text-primary-light">
                SR No <SortHint />
              </th>

              <th className="fw-bold text-primary-light">
                Description <SortHint />
              </th>

              <th className="fw-bold text-primary-light">
                Created Date <SortHint />
              </th>

              <th className="fw-bold text-primary-light">
                Amount <SortHint />
              </th>

              <th className="fw-bold text-primary-light">
                Remarks <SortHint />
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={9}
                  className="text-center py-5 text-secondary-light"
                >
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="text-center py-5 text-secondary-light"
                >
                  No data available in table
                </td>
              </tr>
            ) : (
              rows.map((entry, index) => (
                <tr key={entry._id || index}>
                  <td>
                    {(pageMeta.currentPage - 1) * limitVal + index + 1}
                  </td>

                  <td>{entry.entryName || "—"}</td>

                  <td>{formatDateTime(entry.createdAt)}</td>

                  <td>
                    <span className="text-danger fw-semibold">
                      {formatAmount(entry.amount)}
                    </span>
                  </td>

                  <td>{entry.remarks || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-top d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 p-24">
        <p className="mb-0 text-secondary-light small">
          Showing{" "}
          <span className="fw-medium text-primary-light">
            {startEntry} to {endEntry} of {total} entries
          </span>
        </p>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary radius-8 px-16 py-8 d-inline-flex align-items-center gap-1"
            disabled={!hasPrev}
            onClick={() =>
              setPageNo((prev) => Math.max(1, prev - 1))
            }
          >
            <Icon icon="tabler:chevron-left" className="icon" />
            Prev
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary radius-8 px-16 py-8 d-inline-flex align-items-center gap-1"
            disabled={!hasNext}
            onClick={() => setPageNo((prev) => prev + 1)}
          >
            Next
            <Icon icon="tabler:chevron-right" className="icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashEditDeleteLogLayer;